import React, { Fragment } from 'react';
import {
  Card,
  Typography,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  Box,
  TableCell
} from '@mui/material';
import { TableHeaderCell } from 'src/components/TableHeaderCell';
import { withStyles } from '@mui/styles';
import AddTwoToneIcon from '@mui/icons-material/AddTwoTone';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import { useDispatch, useSelector } from 'react-redux';

import { TableColumnCell } from 'src/components/TableColumnCell';
import MoreVertPopover from '../more-vert-popover';
import CustomTableColumnCell from '../../shared/custom-table-column-cell';
import { TAllDaysTotal, TProject, TTask } from '../../types/time-entry.type';
import {
  ITimeEntry,
  ITimeEntryTableInputProps
} from '../../interface/time-entry.interface';
import {
  saveTimeEntryData,
  updateTimeEntryData,
  updateAssignedTasks,
  requestForRelease
} from 'src/services/timeManagementService';
import SnackBar from 'src/components/SnackBar/SnackBar';
import { AppActions } from 'src/store/AppReducer';
import { ETaskStatus } from '../../enums/task-status.enum';
import { RootStateType } from 'src/store/RootReducer';
import StatusChangePopup from '../status-change-popup';
import ConfirmationModal from 'src/content/common/confirmationModal/ConfirmationModal';

import { useAppSelector } from 'src/hooks/hooks';

const initialTimeEntryDetails: ITimeEntry = {
  employeeId: '',
  projects: [
    {
      projectId: '',
      projectName: '',
      tasks: [
        {
          taskId: '',
          taskName: '',
          daysOfWeeks: [
            {
              day: '',
              entryDate: '',
              hoursWorked: '',
              description: '',
            }
          ],
          planHours: ''
        }
      ]
    }
  ],
  commonTasks: [
    {
      taskId: '',
      taskName: '',
      daysOfWeeks: [
        {
          day: '',
          entryDate: '',
          hoursWorked: '',
          description: '',
        }
      ],
      planHours: ''
    }
  ]
};

const TimeEntryTable: React.FC<ITimeEntryTableInputProps> = ({
  year,
  week,
  timeEntryData,
  getTimeEntryApiMethod,
  version,
  sendValueToParent,
  childValue,
}) => {
  const dispatch = useDispatch();
  const appUser = useSelector((state: RootStateType) => state.appReducer.user);
  const [timeEntryDetails, setTimeEntryDetails] = React.useState<ITimeEntry>(initialTimeEntryDetails);
  const [expand, setExpand] = React.useState<any>();
  const [activeIndexProjectTask, setActiveIndexProjectTask] = React.useState({
    projectIndex: null,
    taskIndex: null,
    timeEntryIndex: null
  });
  const [updatedEntriesProjectTask, setUpdatedEntriesProjectTask] =
    React.useState({});
  const [updatedEntriesCommonTask, setUpdatedEntriesCommonTask] =
    React.useState({});
  const [daysOfWeek, setDaysOfWeek] = React.useState([]);
  const [projectDayTotalHours, setProjectDayTotalHours] = React.useState([]);
  const [dayWiseTotals, setDayWiseTotals] = React.useState({});
  const [overallTotalDayCounts, setOverallTotalDayCounts] = React.useState({
    totalHours: null,
    totalMinutes: null
  });
  const [activeIndexCommonTask, setActiveIndexCommonTask] = React.useState({
    commonTaskIndex: null,
    timeEntryIndex: null
  });
  const [snackBarOpen, setSnackBarOpen] = React.useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = React.useState('');
  const [snackBarMessage, setSnackBarMessage] = React.useState('');
  const [startDate, setStartDate] = React.useState('');
  const [endDate, setEndDate] = React.useState('');
  const [isCloseMoreVertPopover, setIsCloseMoreVertPopover] = React.useState<boolean>(true);
  const [isStatusChangePopupOpen, setIsStatusChangePopupOpen] = React.useState<boolean>(false);
  const [statusChangeAssigneeTaskId, setStatusChangeAssigneeTaskId] = React.useState<number>(0);
  const [height, setHeight] = React.useState(490);
  const [expandStates, setExpandStates] = React.useState({});

  const [pendingStatusChange, setPendingStatusChange] = React.useState<{ assigneeTaskId: number, status: ETaskStatus } | null>(null);
  const [showStatusConfirmation, setShowStatusConfirmation] = React.useState(false);
  const { isLoading } = useAppSelector((state) => state.appReducer);

  const handleStatusChangeRequest = (assigneeTaskId: number,status: ETaskStatus) => {
    setIsCloseMoreVertPopover(!isCloseMoreVertPopover);
    if (childValue) {
      setPendingStatusChange({ assigneeTaskId, status });
      setShowStatusConfirmation(true); 
    } else {
      taskStatusUpdate(assigneeTaskId, status);
    }
  };

  const confirmStatusChange = async () => {
    if (pendingStatusChange) {
      const payload = updateNewTimeEntry();
      try {
        dispatch(AppActions.showLoader());
        await updateTimeEntryData(payload, startDate, endDate);
        taskStatusUpdate(pendingStatusChange.assigneeTaskId, pendingStatusChange.status);
        setPendingStatusChange(null);
      } catch (error) {
        setSnackBarMessage('Failed to save before changing status.');
        setSnackbarSeverity('error');
        setSnackBarOpen(true);
      } finally {
        dispatch(AppActions.hideLoader());
      }
    }
    childValue = false;
    sendValueToParent(false);
    setShowStatusConfirmation(false);
  };

  const confirmStatusChangeWithoutSaving = () => {
    if (pendingStatusChange) {
      taskStatusUpdate(pendingStatusChange.assigneeTaskId, pendingStatusChange.status);
      setPendingStatusChange(null);
    }
    setShowStatusConfirmation(false);
  };

  const handleSetActiveProjectTask = ({
    projectIndex,
    taskIndex,
    timeEntryIndex
  }) => {
    setActiveIndexProjectTask({ projectIndex, taskIndex, timeEntryIndex });
    setActiveIndexCommonTask({ commonTaskIndex: null, timeEntryIndex: null });
  };

  const handleSetActiveCommonTask = ({ commonTaskIndex, timeEntryIndex }) => {
    setActiveIndexCommonTask({ commonTaskIndex, timeEntryIndex });
    setActiveIndexProjectTask({ projectIndex: null, taskIndex: null, timeEntryIndex: null });
  };

  const handleExpand = (id) => {
    setExpand({
      id: id,
      expand: expand?.id === id ? !expand.expand : true
    });
  };

  const handleCategoryExpand = (type, projectId) => {
    setExpandStates(prevStates => ({
      ...prevStates,
      [type]: {
        id: projectId,
        expand: prevStates[type]?.id === projectId ? !prevStates[type].expand : true,
      }
    }));
  };

  // If there is a new category, you need to add here
  const taskTypes = ['DEV', 'QA', 'BA', 'GENERIC', 'MISCELLANEOUS'];

  const handleTimeChangeProjectTask = (
    projectIndex,
    taskIndex,
    timeEntryIndex,
    updatedTime
  ) => {
    setUpdatedEntriesProjectTask((prev) => {
      const updated = { ...prev };

      if (!updated[projectIndex]) {
        updated[projectIndex] = {};
      }

      if (!updated[projectIndex][taskIndex]) {
        updated[projectIndex][taskIndex] = {};
      }
      updated[projectIndex][taskIndex][timeEntryIndex] = updatedTime;
      return updated;
    });

    if (updatedTime.hoursWorked  !== '0h 0m') {
      sendValueToParent(true);
    }else{
      sendValueToParent(false);
    }
  };

  const handleTimeChangeCommonTask = (
    commonTaskIndex,
    timeEntryIndex,
    updatedTime
  ) => {
    setUpdatedEntriesCommonTask((prev) => {
      const updated = { ...prev };

      if (!updated[commonTaskIndex]) {
        updated[commonTaskIndex] = {};
      }
      updated[commonTaskIndex][timeEntryIndex] = updatedTime;
      return updated;
    });

     if (updatedTime.hoursWorked  !== '0h 0m') {
      sendValueToParent(true);
    }else{
      sendValueToParent(false);
    }
  };

  const getDatesOfWeek = (week) => {
    let startDate = new Date(week?.weekStartDate);

    let day = startDate.getDay();
    let daysToMonday = day === 0 ? -6 : 1 - day;
    startDate.setDate(startDate.getDate() + daysToMonday);

    let datesOfWeek = [];
    for (let i = 0; i < 7; i++) {
      datesOfWeek.push(new Date(startDate));
      startDate.setDate(startDate.getDate() + 1);
    }

    return datesOfWeek;
  };

  const getDayName = (date) => {
    const dayNames = [
      'Sun',
      'Mon',
      'Tues',
      'Wed',
      'Thurs',
      'Fri',
      'Sat'
    ];
    return dayNames[date.getDay()];
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-CA');
  };

  //clear active time entry index
  const onClearActiveTimeEntryIndex = () => {
    setActiveIndexProjectTask({
      projectIndex: null,
      taskIndex: null,
      timeEntryIndex: null
    });

    setActiveIndexCommonTask({
      commonTaskIndex: null,
      timeEntryIndex: null
    });
  }

  const onIsActiveChange = (isShow) => {
    setTimeout(() => {
      onClearActiveTimeEntryIndex();
    }, 500);
  }

  const handleSubmit = async () => {
    onClearActiveTimeEntryIndex();

    const requestBody: ITimeEntry = await updateNewTimeEntry();

    // set api condition
    let data: ITimeEntry = timeEntryDetails;
    let hasTimeEntryId = false;

    // Check `projects` array
    if (data.projects && data.projects.length > 0) {
      for (const project of data.projects) {
        for (const task of project.tasks) {
          if (task.timeEntryId !== 0) {
            hasTimeEntryId = true;
            break;
          }
        }
        if (hasTimeEntryId) break;
      }
    }

    // Check `commonTasks` array
    if (!hasTimeEntryId && data.commonTasks && data.commonTasks.length > 0) {
      for (const task of data.commonTasks) {
        if (task.timeEntryId !== 0) {
          hasTimeEntryId = true;
          break;
        }
      }
    }

    if (hasTimeEntryId) {
      updateTimeEntryApiMethod(requestBody);
    } else {
      saveTimeEntryApiMethod(requestBody);
    }
  };

  const saveTimeEntryApiMethod = (payload: ITimeEntry) => {
    dispatch(AppActions.showLoader());
    saveTimeEntryData(payload)
      .then((response) => {
        dispatch(AppActions.hideLoader());
        setSnackBarMessage('You’ve saved time entry details successfully.');
        setSnackbarSeverity('success');
        setSnackBarOpen(true);
        getTimeEntryApiMethod();
      })
      .catch((error) => {
        dispatch(AppActions.hideLoader());
        setSnackBarMessage('Unable to save time entry details.');
        setSnackbarSeverity('error');
        setSnackBarOpen(true);
      });
  };

  const updateTimeEntryApiMethod = (payload: ITimeEntry) => {
    dispatch(AppActions.showLoader());
    updateTimeEntryData(payload, startDate, endDate)
      .then((response) => {
        dispatch(AppActions.hideLoader());
        setSnackBarMessage('You’ve saved time entry details successfully.');
        setSnackbarSeverity('success');
        setSnackBarOpen(true);
        getTimeEntryApiMethod();
      })
      .catch((error) => {
        dispatch(AppActions.hideLoader());
        setSnackBarMessage('Unable to save time entry details.');
        setSnackbarSeverity('error');
        setSnackBarOpen(true);
      });
  };

  const updateNewTimeEntry = () => {
    const requestBodyProjectsTask = [];

    timeEntryDetails?.projects.forEach((project, projectIndex) => {
      const updatedProject = {
        projectId: project.projectId,
        projectName: project.projectName,
        tasks: project.tasks.map((task, taskIndex) => {
          return {
            ...task,
            daysOfWeeks: task.daysOfWeeks.map((entry, timeEntryIndex) => {
              return (
                updatedEntriesProjectTask[projectIndex]?.[taskIndex]?.[
                  timeEntryIndex
                ] || entry
              );
            })
          };
        })
      };
      requestBodyProjectsTask.push(updatedProject);
    });

    const updatedCommonTasks = timeEntryDetails?.commonTasks.map(
      (commonTask, commonTaskIndex) => {
        return {
          ...commonTask,
          daysOfWeeks: commonTask.daysOfWeeks.map((entry, timeEntryIndex) => {
            return (
              updatedEntriesCommonTask[commonTaskIndex]?.[timeEntryIndex] ||
              entry
            );
          })
        };
      }
    );

    const requestBody: ITimeEntry = {
      employeeId: timeEntryDetails?.employeeId,
      projects: requestBodyProjectsTask,
      commonTasks: updatedCommonTasks
    };

    setTimeEntryDetails(requestBody);
    return requestBody;
  };

  const calculateProjectDayTotalHours = (projects: TProject[]) => {
    return projects.map((project) => {
      const dayOnProjectTotalHours: Record<
        string,
        { hours: number; minutes: number }
      > = {};

      project.tasks.forEach((task) => {
        task.daysOfWeeks.forEach((entry) => {
          if (entry.hoursWorked) {
            const [hours, minutes] = entry.hoursWorked
              .split('h')
              .map((part) => parseInt(part.replace('m', '').trim()));

              const lowerCaseDay = entry.day.toLowerCase();

            if (!dayOnProjectTotalHours[lowerCaseDay]) {
              dayOnProjectTotalHours[lowerCaseDay] = { hours: 0, minutes: 0 };
            }

            dayOnProjectTotalHours[lowerCaseDay].hours += hours;
            dayOnProjectTotalHours[lowerCaseDay].minutes += minutes;

            if (dayOnProjectTotalHours[lowerCaseDay].minutes >= 60) {
              dayOnProjectTotalHours[lowerCaseDay].hours += Math.floor(
                dayOnProjectTotalHours[lowerCaseDay].minutes / 60
              );
              dayOnProjectTotalHours[lowerCaseDay].minutes %= 60;
            }
          }
        });
      });

      const formattedTotalHours = Object.entries(dayOnProjectTotalHours).reduce(
        (acc, [day, { hours, minutes }]) => {
          acc[day] = `${hours}h ${minutes}m`;
          return acc;
        },
        {} as Record<string, string>
      );

      return {
        projectId: project.projectId,
        dayOnProjectTotalHours: formattedTotalHours
      };
    });
  };

  const calculateTaskWiseTotalHours = (projects: TProject[]) => {
    return projects.flatMap((project) => 
      project.tasks.map((task) => {
        const totalHours: { hours: number; minutes: number } = { hours: 0, minutes: 0 };
  
        task.daysOfWeeks.forEach((entry) => {
          if (entry.hoursWorked) {
            const [hours, minutes] = entry.hoursWorked
              .split('h')
              .map((part) => parseInt(part.replace('m', '').trim()));
  
            totalHours.hours += hours;
            totalHours.minutes += minutes;
  
            // Adjust for minutes exceeding 60
            if (totalHours.minutes >= 60) {
              totalHours.hours += Math.floor(totalHours.minutes / 60);
              totalHours.minutes %= 60;
            }
          }
        });
  
        return {
          taskId: task.taskId,
          taskName: task.taskName,
          totalHours: formatTotalTime(totalHours.hours, totalHours.minutes),
        };
      })
    );
  };

  const calculateCommonTaskWiseTotalHours = (commonTasks: TTask[]) => {
    return commonTasks.flatMap((task) => {
      const totalHours: { hours: number; minutes: number } = { hours: 0, minutes: 0 };
  
      task.daysOfWeeks.forEach((entry) => {
        if (entry.hoursWorked) {
          const [hours, minutes] = entry.hoursWorked
            .split('h')
            .map((part) => parseInt(part.replace('m', '').trim()));
  
          totalHours.hours += hours;
          totalHours.minutes += minutes;
  
          if (totalHours.minutes >= 60) {
            totalHours.hours += Math.floor(totalHours.minutes / 60);
            totalHours.minutes %= 60;
          }
        }
      });
  
      return {
        taskId: task.taskId,
        taskName: task.taskName,
        totalHours: formatTotalTime(totalHours.hours, totalHours.minutes),
      };
    });
  };

  const calculateWeeklyTotalHoursForProject = (dayOnProjectTotalHours) => {
    const totalHours = { hours: 0, minutes: 0 };

    const parseTimeString = (timeString) => {
      if (typeof timeString !== 'string') {
        return { hours: 0, minutes: 0 };
      }

      const hoursMatch = timeString.match(/(\d+)h/);
      const minutesMatch = timeString.match(/(\d+)m/);
      const hours = hoursMatch ? parseInt(hoursMatch[1], 10) : 0;
      const minutes = minutesMatch ? parseInt(minutesMatch[1], 10) : 0;

      return { hours, minutes };
    };

    Object.values(dayOnProjectTotalHours || {}).forEach((timeString) => {
      const { hours, minutes } = parseTimeString(timeString);
      totalHours.hours += hours;
      totalHours.minutes += minutes;

      if (totalHours.minutes >= 60) {
        totalHours.hours += Math.floor(totalHours.minutes / 60);
        totalHours.minutes %= 60;
      }
    });

    return `${totalHours.hours}h ${totalHours.minutes}m`;
  };

  const calculateDayWiseTotal = () => {
    const dayWiseTotal = {};

    timeEntryDetails?.projects.forEach((project) => {
      project.tasks.forEach((task) => {
        task.daysOfWeeks.forEach((entry, index) => {
          if (entry.hoursWorked) {
            const [hours, minutes] = entry.hoursWorked
              .split(' ')
              .map((part) => parseInt(part.replace(/\D/g, '')));
            if (!dayWiseTotal[index]) {
              dayWiseTotal[index] = { hours: 0, minutes: 0 };
            }
            dayWiseTotal[index].hours += hours;
            dayWiseTotal[index].minutes += minutes;
          }
        });
      });
    });

    timeEntryDetails?.commonTasks?.forEach((common) => {
      common.daysOfWeeks.forEach((entry, index) => {
        if (entry.hoursWorked) {
          const [hours, minutes] = entry.hoursWorked
            .split(' ')
            .map((part) => parseInt(part.replace(/\D/g, '')));
          if (!dayWiseTotal[index]) {
            dayWiseTotal[index] = { hours: 0, minutes: 0 };
          }
          dayWiseTotal[index].hours += hours;
          dayWiseTotal[index].minutes += minutes;
        }
      });
    });

    for (const day in dayWiseTotal) {
      const totalMinutes = dayWiseTotal[day].minutes;
      const extraHours = Math.floor(totalMinutes / 60);
      dayWiseTotal[day].hours += extraHours;
      dayWiseTotal[day].minutes = totalMinutes % 60;
    }

    return dayWiseTotal;
  };

  const formatTotalTime = (hours, minutes) => {
    return `${hours}h ${minutes}m`;
  };

  const calculateOverallTotal = (allDaysTotal: TAllDaysTotal) => {
    let totalHours = 0;
    let totalMinutes = 0;

    Object.values(allDaysTotal).forEach(({ hours, minutes }) => {
      totalHours += hours;
      totalMinutes += minutes;
    });

    const extraHours = Math.floor(totalMinutes / 60);
    totalHours += extraHours;
    totalMinutes = totalMinutes % 60;

    return { totalHours, totalMinutes };
  };

  const toggleStatusChangePopup = () => {
    setIsStatusChangePopupOpen(!isStatusChangePopupOpen);
  }

  const taskStatusUpdate = (assigneeTaskId: number, status: ETaskStatus) => {

    dispatch(AppActions.showLoader());
    const payload = [
      {
        id: assigneeTaskId,
        status: status
      }
    ];
    updateAssignedTasks(payload)
      .then((res) => {
        dispatch(AppActions.hideLoader());
        setSnackBarMessage('You’ve update status successfully.');
        setSnackbarSeverity('success');
        setSnackBarOpen(true);
        getTimeEntryApiMethod();
      })
      .catch((err) => {
        dispatch(AppActions.hideLoader());
        setSnackBarMessage('Unable to update status.');
        setSnackbarSeverity('error');
        setSnackBarOpen(true);
      });
  };

  const requestForReleaseApiMethod = (status: ETaskStatus) => {
    const payload = { assigneeTaskId: statusChangeAssigneeTaskId, status: status };
    
    dispatch(AppActions.showLoader());
    requestForRelease(payload)
      .then((res) => {
        dispatch(AppActions.hideLoader());
        toggleStatusChangePopup();
        setSnackBarMessage('Status change request sent successfully.');
        setSnackbarSeverity('success');
        setSnackBarOpen(true);
        getTimeEntryApiMethod();
      })
      .catch((err) => {
        dispatch(AppActions.hideLoader());
        toggleStatusChangePopup();
        setSnackBarMessage('Unable to sent status change request.');
        setSnackbarSeverity('error');
        setSnackBarOpen(true);
      });
  };

  React.useEffect(() => {
    if (timeEntryData) {
      //clear entered data
      setUpdatedEntriesProjectTask({});
      setUpdatedEntriesCommonTask({});
      onClearActiveTimeEntryIndex();

      setTimeEntryDetails(timeEntryData);
    }
  }, [timeEntryData]);

  React.useEffect(() => {
    if (version > 0) {
      handleSubmit();
    }
  }, [version]);

  React.useEffect(() => {
    if (dayWiseTotals) {
      setOverallTotalDayCounts(calculateOverallTotal(dayWiseTotals));
    }
  }, [dayWiseTotals]);

  React.useEffect(() => {
    if (timeEntryDetails) {
      setProjectDayTotalHours(calculateProjectDayTotalHours(timeEntryDetails?.projects));
      setDayWiseTotals(calculateDayWiseTotal());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeEntryDetails]);

  React.useEffect(() => {
    updateNewTimeEntry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndexProjectTask, activeIndexCommonTask]);

  React.useEffect(() => {
    const weeks = getDatesOfWeek(week);
    setDaysOfWeek(weeks);
    setStartDate(formatDate(week.weekStartDate));
    setEndDate(formatDate(week.weekEndDate));
  }, [year, week]);

  const getConditionalHeight = () => {
    const screenHeight = window.innerHeight;
    if (screenHeight < 900) {
      return 490;
    }  else {
      return 710;
    }
  }

  React.useEffect(() => {
    const handleResize = () => {
      const tableHeightValue = getConditionalHeight();
      setHeight(tableHeightValue);
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const CustomTableRow = withStyles({
    root: {
      background: '#E6E9FF !important',
      height: '3rem'
    }
  })(TableRow);

  const doesTaskExist = (taskType, projectId) => {
    return timeEntryDetails?.projects.some(
      (project) =>
        project.projectId === projectId &&
        project.tasks.some(
          (task) => task.taskType === taskType && task.daysOfWeeks.length > 0
        )
    );
  };

  return (
    <Fragment>
      <div className="time-entry-table">
        <Card style={{ borderRadius: '3px' }}>
          <TableContainer className="tableContainer" style={{ maxHeight: `${height}px`, overflowY: 'auto' }}>
            <Table
              style={{ borderCollapse: 'separate'  }}
              size="small"
            >
              <TableHead style={{ position: 'sticky', top: 0, zIndex: 10}}>
                <CustomTableRow
                >
                  <TableHeaderCell content="" smallFont sticky leftSticky />
                  {daysOfWeek?.length > 0 &&
                    daysOfWeek?.map((d, i) => {
                      return (
                        <TableCell
                          key={i}
                          style={{
                            width: '180px',
                            whiteSpace: 'nowrap',
                            alignItems: 'center',
                            color: '#232C68',
                            fontWeight: '700',
                            fontSize: '11px',
                            background: '#E6E9FF',
                            borderRight: `${
                              d.dayName === 'Sunday'
                                ? '1px solid black'
                                : 'auto'
                            }`,
                            borderLeft: `${
                              d.dayName === 'Mon'
                                ? '1px solid black'
                                : '1px solid rgba(34, 51, 84, 0.1)'
                            }`
                          }}
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            flexDirection="column"
                          >
                            <Typography
                              mb={0}
                              variant="body2"
                              style={{ fontSize: '11px', fontWeight: '700' }}
                              gutterBottom
                              noWrap
                            >{getDayName(d)}{" - "}{`${d.toLocaleString('default', {
                                month: 'short'
                              })} ${d.getDate()}`}
                            </Typography>
                          </Box>
                        </TableCell>
                      );
                    })}
                  <TableCell
                    style={{
                      width: '100px',
                      whiteSpace: 'nowrap',
                      textAlign: 'center',
                      color: '#232C68',
                      fontWeight: '900',
                      fontSize: '11px',
                      background: 'rgba(98, 103, 253, 0.2)'


                    }}
                  >
                    Total
                  </TableCell>
                  <TableCell
                    style={{
                      whiteSpace: 'nowrap',
                      textAlign:'center',
                      color: '#232C68',
                      fontWeight: '700',
                      fontSize: '11px',
                      background: '#E6E9FF',
                      paddingLeft: '4px',
                      paddingRight: '4px',
                      width:' 20px'
                    }}
                  >
                    <Button
                      sx={{
                        mt: { xs: 2, md: 0 },
                        fontSize: '11px',
                        fontWeight: 'normal'
                      }}
                      variant="contained"
                      style={{ height: '1.75rem',minWidth:'48px',paddingLeft:'10px',paddingRight:'10px' }}
                      id="btn-downloadReport"
                      onClick={() => {handleSubmit(); sendValueToParent(false);}}
                    >
                      Save
                    </Button>
                  </TableCell>
                </CustomTableRow>
              </TableHead>
              <TableHead style={{ position: 'sticky', top: '3rem', zIndex: 10}}>
                <CustomTableRow
                >
                  <TableHeaderCell
                    content="Overall Total Hours"
                    smallFont
                    sticky
                    leftSticky
                  />
                  {Object.keys(dayWiseTotals).map((dayIndex) => (
                    <TableHeaderCell
                      key={dayIndex}
                      content={formatTotalTime(
                        dayWiseTotals[dayIndex].hours,
                        dayWiseTotals[dayIndex].minutes
                      )}
                      justifyContent={true}
                      smallFont
                      sticky
                    />
                  ))}
                  <TableHeaderCell
                    content={formatTotalTime(
                      overallTotalDayCounts.totalHours,
                      overallTotalDayCounts.totalMinutes
                    )}
                    justifyContent={true}
                    smallFont
                    totalColumnStyle = {true}
                  />
                   <TableHeaderCell
                    content={''}
                    justifyContent={true}
                    smallFont
                  />
                </CustomTableRow>
              </TableHead>
              <TableBody>
                <>
                  {timeEntryDetails &&
                    timeEntryDetails?.projects?.length > 0 &&
                    timeEntryDetails?.projects.map((project, projectIndex) => {
                      const dayOnProjectTotalHours =
                        projectDayTotalHours.length > 0 &&
                        projectDayTotalHours.find(
                          (p) => p.projectId === project.projectId
                        )?.dayOnProjectTotalHours;

                      const projectWeeklyTotalHours =
                        calculateWeeklyTotalHoursForProject(
                          dayOnProjectTotalHours
                        );

                      return (
                        <Fragment key={project.projectId}>
                          <TableRow
                            hover
                            key={project.projectId}
                            className="main-row"
                          >
                            <TableColumnCell className="tree-btn-clm">
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center'
                                }}
                              >
                                <div style={{ width: '40px' }}>
                                  {project?.tasks?.length > 0 && (
                                    <Button
                                      sx={{
                                        mt: { xs: 2, md: 0 },
                                        fontSize: '11px',
                                        fontWeight: 'normal',
                                        width: '14px',
                                        height: '14px',
                                        padding: 0,
                                        minWidth: 'unset',
                                        borderRadius: '2px',
                                        cursor: 'pointer'
                                      }}
                                      onClick={(e) =>
                                        handleExpand(project.projectId)
                                      }
                                      variant="contained"
                                      startIcon={
                                        expand?.id === project?.projectId &&
                                        expand?.expand ? (
                                          <HorizontalRuleIcon
                                            fontSize="small"
                                            style={{ fontSize: '14px' }}
                                          />
                                        ) : (
                                          <AddTwoToneIcon
                                            fontSize="small"
                                            style={{
                                              fontSize: '14px',
                                              margin: 0
                                            }}
                                          />
                                        )
                                      }
                                    />
                                  )}
                                </div>
                                <div className="border-0">
                                  <Typography
                                    marginBottom={0}
                                    paddingBottom="0.25rem"
                                    paddingTop="0.5rem"
                                    variant="body2"
                                    style={{ fontSize: '11px', fontWeight: '700' }}
                                    color="text.primary"
                                    gutterBottom
                                    noWrap
                                  >
                                    {project?.projectName}
                                  </Typography>
                                </div>
                              </div>
                            </TableColumnCell>
                            <CustomTableColumnCell
                              content={
                                dayOnProjectTotalHours?.monday || '0h 0m'
                              }
                              sticky={false}
                              isHeaderRow={true}
                              className="text-center"
                            />
                            <CustomTableColumnCell
                              content={
                                dayOnProjectTotalHours?.tuesday || '0h 0m'
                              }
                              sticky={false}
                              isHeaderRow={true}
                              className="text-center"
                            />
                            <CustomTableColumnCell
                              content={
                                dayOnProjectTotalHours?.wednesday || '0h 0m'
                              }
                              sticky={false}
                              isHeaderRow={true}
                              className="text-center"
                            />
                            <CustomTableColumnCell
                              content={
                                dayOnProjectTotalHours?.thursday || '0h 0m'
                              }
                              sticky={false}
                              isHeaderRow={true}
                              className="text-center"
                            />
                            <CustomTableColumnCell
                              content={
                                dayOnProjectTotalHours?.friday || '0h 0m'
                              }
                              sticky={false}
                              isHeaderRow={true}
                              className="text-center"
                            />
                            <CustomTableColumnCell
                              content={
                                dayOnProjectTotalHours?.saturday || '0h 0m'
                              }
                              sticky={false}
                              isHeaderRow={true}
                              className="text-center"
                            />
                            <CustomTableColumnCell
                              content={
                                dayOnProjectTotalHours?.sunday || '0h 0m'
                              }
                              sticky={false}
                              isHeaderRow={true}
                              className="text-center"
                            />
                            <CustomTableColumnCell
                              content={projectWeeklyTotalHours || '0h 0m'}
                              sticky={false}
                              isHeaderRow={true}
                              totalColumnStyle={true}
                            />
                             <CustomTableColumnCell
                              content={''}
                              sticky={false}
                              isHeaderRow={true}
                              className="text-center"                           
                            />
                          </TableRow>
                          {expand?.id === project?.projectId &&
                            expand?.expand &&
                            project?.tasks.length > 0 && (
                              <>
                                {taskTypes.map(type => (
                                  <>
                                    {doesTaskExist(type, project?.projectId) && (
                                      <TableRow
                                        hover
                                        key={project.projectId}
                                        className="main-row"
                                      >
                                        <TableColumnCell className="tree-btn-clm">
                                          <div
                                            style={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              marginLeft: '6px'
                                            }}
                                          >
                                            <div style={{ width: '40px' }}>
                                              {project?.tasks?.length > 0 && (
                                                <Button
                                                  sx={{
                                                    mt: { xs: 2, md: 0 },
                                                    fontSize: '11px',
                                                    fontWeight: 'normal',
                                                    width: '14px',
                                                    height: '14px',
                                                    padding: 0,
                                                    minWidth: 'unset',
                                                    borderRadius: '2px',
                                                    cursor: 'pointer'
                                                  }}
                                                  onClick={(e) =>
                                                    handleCategoryExpand(type, project.projectId)
                                                  }
                                                  variant="contained"
                                                  startIcon={
                                                    expandStates[type]?.id === project.projectId && expandStates[type]?.expand ? (
                                                      <HorizontalRuleIcon
                                                        fontSize="small"
                                                        style={{ fontSize: '14px' }}
                                                      />
                                                    ) : (
                                                      <AddTwoToneIcon
                                                        fontSize="small"
                                                        style={{
                                                          fontSize: '14px',
                                                          margin: 0
                                                        }}
                                                      />
                                                    )
                                                  }
                                                />
                                              )}
                                            </div>
                                            <div className="border-0">
                                              <Typography
                                                marginBottom={0}
                                                paddingBottom="0.25rem"
                                                paddingTop="0.5rem"
                                                variant="body2"
                                                style={{ fontSize: '11px', fontWeight: '700' }}
                                                color="text.primary"
                                                gutterBottom
                                                noWrap
                                              >
                                                {type === "MISCELLANEOUS" ? "MISC" : type}
                                              </Typography>
                                            </div>
                                          </div>
                                        </TableColumnCell>
                                        <CustomTableColumnCell
                                          content={
                                            ' '
                                          }
                                          sticky={false}
                                          isHeaderRow={true}
                                          className="text-center"
                                        />
                                        <CustomTableColumnCell
                                          content={
                                            ' '
                                          }
                                          sticky={false}
                                          isHeaderRow={true}
                                          className="text-center"
                                        />
                                        <CustomTableColumnCell
                                          content={
                                            ' '
                                          }
                                          sticky={false}
                                          isHeaderRow={true}
                                          className="text-center"
                                        />
                                        <CustomTableColumnCell
                                          content={
                                            ' '
                                          }
                                          sticky={false}
                                          isHeaderRow={true}
                                          className="text-center"
                                        />
                                        <CustomTableColumnCell
                                          content={
                                            ' '
                                          }
                                          sticky={false}
                                          isHeaderRow={true}
                                          className="text-center"
                                        />
                                        <CustomTableColumnCell
                                          content={
                                            ' '
                                          }
                                          sticky={false}
                                          isHeaderRow={true}
                                          className="text-center"
                                        />
                                        <CustomTableColumnCell
                                          content={
                                            ' '
                                          }
                                          sticky={false}
                                          isHeaderRow={true}
                                          className="text-center"
                                        />
                                        <CustomTableColumnCell
                                          content={''}
                                          sticky={false}
                                          isHeaderRow={true}
                                          className="text-center"
                                        />
                                        <CustomTableColumnCell
                                          content={''}
                                          sticky={false}
                                          isHeaderRow={true}
                                          className="text-center"
                                        />
                                      </TableRow>
                                    )}
                                    {expandStates[type]?.id === project?.projectId &&
                                      expandStates[type]?.expand && project.tasks.map((task, taskIndex) => {
                                        return (
                                          <>
                                            {task.taskType === type && (
                                              <TableRow hover key={taskIndex}>
                                                <CustomTableColumnCell
                                                  content={task?.taskName}
                                                  planHours={task?.planHours}
                                                  sticky={false}
                                                  isTaskNameColumn={true}
                                                  status={task?.status}
                                                />
                                                {task?.daysOfWeeks?.length > 0 &&
                                                  task?.daysOfWeeks?.map(
                                                    (entry, timeEntryIndex) => {
                                                      const isActive =
                                                        activeIndexProjectTask?.taskIndex ===
                                                        taskIndex &&
                                                        activeIndexProjectTask?.timeEntryIndex ===
                                                        timeEntryIndex;

                                                      return (
                                                        <CustomTableColumnCell
                                                          key={timeEntryIndex}
                                                          dataOfDate={entry}
                                                          status={task?.status}
                                                          content={entry.hoursWorked}
                                                          sticky={false}
                                                          description={entry.description}
                                                          className="text-center"
                                                          isEditable={true}
                                                          isActive={isActive}
                                                          isTaskNameColumn={false}
                                                          onEditClick={() =>
                                                            handleSetActiveProjectTask({
                                                              projectIndex,
                                                              taskIndex,
                                                              timeEntryIndex
                                                            })
                                                          }
                                                          onTimeChange={(updatedTime) =>
                                                            handleTimeChangeProjectTask(
                                                              projectIndex,
                                                              taskIndex,
                                                              timeEntryIndex,
                                                              updatedTime
                                                            )
                                                          }
                                                          onIsActiveChange={onIsActiveChange}
                                                        />
                                                      );
                                                    }
                                                  )}
                                                <TableCell style={{ height: '100%', borderRight: '1px solid rgba(34, 51, 84, 0.1)',background:'rgba(98, 103, 253, 0.2)' }}>
                                                  <div
                                                    style={{
                                                      display: 'flex',
                                                      justifyContent: 'center',
                                                      alignItems: 'center',
                                                      height: '100%',
                                                    }}
                                                  >
                                                    <div
                                                      style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        fontWeight: '900',
                                                   
                                                        height: '100%',
                                                        whiteSpace: 'nowrap',
                                                
                                                        fontSize: '11px'
                                                      }}
                                                    >
                                                      {calculateTaskWiseTotalHours([project]).find(t => t.taskId === task.taskId)?.totalHours || '0h 0m'}
                                                    </div>
                                                    
                                                  </div>
                                                </TableCell>
                                                <TableCell style={{ height: '100%'
                                                 }}>
                                                    <MoreVertPopover
                                                      index={projectIndex}
                                                      status={task.status}
                                                      handleToDoBtn={() => {                                                       
                                                        handleStatusChangeRequest(task.assigneeTaskId, ETaskStatus.TO_DO);
                                                      }}
                                                      handleInProgressBtn={() => {                                                       
                                                        handleStatusChangeRequest(task.assigneeTaskId, ETaskStatus.IN_PROGRESS);
                                                      }}
                                                      handleOnHoldBtn={() => {
                                                        handleStatusChangeRequest(task.assigneeTaskId, ETaskStatus.ON_HOLD);
                                                      }}
                                                      handleCompleteBtn={() => {
                                                        handleStatusChangeRequest(task.assigneeTaskId, ETaskStatus.COMPLETED);
                                                      }}
                                                      handleRequestBtn={() => {
                                                        toggleStatusChangePopup();
                                                        setIsCloseMoreVertPopover(!isCloseMoreVertPopover);
                                                        setStatusChangeAssigneeTaskId(task.assigneeTaskId);
                                                      }}
                                                      isClosePopup={isCloseMoreVertPopover}
                                                    />
                                                </TableCell>
                                              </TableRow>
                                            )}
                                          </>
                                        )
                                      }
                                      )}
                                  </>
                                ))}
                              </>
                            )
                          }
                        </Fragment>
                      );
                    })}
                </>
                <>
                  {timeEntryDetails &&
                    timeEntryDetails?.commonTasks?.length > 0 &&
                    timeEntryDetails?.commonTasks.map(
                      (commonTask, commonTaskIndex) => {
                        return (
                          <TableRow hover key={commonTaskIndex}>
                            <CustomTableColumnCell
                              content={commonTask?.taskName}
                              planHours={commonTask?.planHours}
                              sticky={false}
                              isTaskNameColumn={true}
                              isCommonTask={true}
                              status={commonTask?.status}
                            />
                            {commonTask?.daysOfWeeks?.length > 0 &&
                              commonTask?.daysOfWeeks?.map(
                                (entry, timeEntryIndex) => {
                                  const isActive =
                                    activeIndexCommonTask?.commonTaskIndex ===
                                      commonTaskIndex &&
                                    activeIndexCommonTask?.timeEntryIndex ===
                                    timeEntryIndex;

                                  return (
                                    <CustomTableColumnCell
                                      key={timeEntryIndex}
                                      dataOfDate={entry}
                                      status={commonTask?.status}
                                      content={entry.hoursWorked}
                                      sticky={false}
                                      className="text-center"
                                      isEditable={true}
                                      isActive={isActive}
                                      isCommonTask={true}
                                      description={entry.description}
                                      onEditClick={() =>
                                        handleSetActiveCommonTask({
                                          commonTaskIndex,
                                          timeEntryIndex
                                        })
                                      }
                                      onTimeChange={(updatedTime) =>
                                        handleTimeChangeCommonTask(
                                          commonTaskIndex,
                                          timeEntryIndex,
                                          updatedTime
                                        )
                                      }
                                      onIsActiveChange={onIsActiveChange}
                                    />
                                  );
                                }
                              )}
                            <CustomTableColumnCell
                              sticky={true}
                              content={calculateCommonTaskWiseTotalHours([commonTask]).find(t => t.taskName === commonTask.taskName)?.totalHours || '0h 0m'}
                              isHeaderRow={true}
                              isCommonTask={true}
                              totalColumnStyle={true}
                            />
                            <CustomTableColumnCell
                              sticky={true}
                              content={''}
                              isHeaderRow={true}
                              isCommonTask={true}
                            />
                            
                          </TableRow>
                        );
                      }
                    )}
                </>
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
        <SnackBar
          snackBarOpen={snackBarOpen}
          setSnackBarOpen={setSnackBarOpen}
          snackBarMessage={snackBarMessage}
          snackbarSeverity={snackbarSeverity}
        />
        <StatusChangePopup
          open={isStatusChangePopupOpen}
          toggle={toggleStatusChangePopup}
          onConfirm={requestForReleaseApiMethod}
        />
      </div>
      <ConfirmationModal
        open={showStatusConfirmation}
        toggle={() => setShowStatusConfirmation(false)}
        message={`There are unsaved changes. Do you want to save before changing status?`}
        onConfirm={confirmStatusChange}
        noToggle={confirmStatusChangeWithoutSaving}
        isLoading={isLoading}
        button1="Discard"
        button2="Save"
      />
    </Fragment>
  );
};

export default TimeEntryTable;
